// Seedable PRNG used by the game engine.
//
// We use mulberry32 — a small, fast 32-bit PRNG with good statistical
// properties for game-scale use (deck shuffles, coin flips, random target
// selection). Its state is a single uint32, which serializes cleanly into
// the GameRecord and clones cheaply for AI tree search.
//
// `draws` is bookkeeping: it lets a replay tool detect divergence by step
// rather than by final-state hash alone, and helps diagnose seed plumbing
// bugs (e.g. "the AI consumed 3 draws that the live game didn't").

export interface PRNGState {
  state: number;  // uint32
  draws: number;
}

const UINT32 = 0x100000000;  // 2^32

/** Create a new PRNG state from a 32-bit seed. Seed 0 is allowed but maps
 *  internally to 1 to avoid mulberry32's all-zero fixed point. */
export function createPRNG(seed: number): PRNGState {
  const s = (seed | 0) >>> 0;
  return { state: s === 0 ? 1 : s, draws: 0 };
}

export function clonePRNG(rng: PRNGState): PRNGState {
  return { state: rng.state, draws: rng.draws };
}

/** Mutates `rng`. Returns a float in [0, 1). */
export function nextFloat(rng: PRNGState): number {
  let t = (rng.state + 0x6D2B79F5) >>> 0;
  rng.state = t;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  rng.draws++;
  return ((t ^ (t >>> 14)) >>> 0) / UINT32;
}

/** Mutates `rng`. Returns an integer in [0, max). Throws on max <= 0. */
export function nextInt(rng: PRNGState, max: number): number {
  if (max <= 0) throw new Error(`nextInt: max must be > 0 (got ${max})`);
  return Math.floor(nextFloat(rng) * max);
}

/** Fisher-Yates shuffle using the supplied PRNG. Mutates `rng`; returns a
 *  fresh array (does not touch the input). */
export function shuffleWith<T>(rng: PRNGState, array: T[]): T[] {
  const a = [...array];
  for (let i = a.length - 1; i > 0; i--) {
    const j = nextInt(rng, i + 1);
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/** Generate a non-zero 32-bit seed from Date.now() XOR Math.random(). This
 *  is the one place we deliberately consult the system RNG — for a brand-new
 *  game where no seed was provided. The chosen seed is recorded in the
 *  GameRecord so subsequent replays are deterministic. */
export function generateSeed(): number {
  const a = (Date.now() & 0xFFFFFFFF) >>> 0;
  const b = (Math.floor(Math.random() * UINT32)) >>> 0;
  const s = (a ^ b) >>> 0;
  return s === 0 ? 1 : s;
}
